// apps/web/src/pages/estoque/saldos-page.tsx — Fase 13, Plano 07 (EST-08, EST-10, D-13).
// Tela "Saldos de estoque" do estoque PRÓPRIO da Pieltec: saldo por matéria-prima x localização, custo médio e valor.
// Busca, Localização, Tipo, "Mostrar itens sem saldo" e paginação são resolvidos no servidor; o servidor também calcula
// saldo, custo médio, valor e total. Esta tela só apresenta. Material de cliente NUNCA aparece na aba "Estoque próprio":
// ele tem aba própria, "Material de clientes" (`?aba=clientes`, Plano 14), com lista e valorização (pela NF) separadas (D-23).
import { Fragment, useCallback, useEffect, useRef, useState, type FormEvent, type MouseEvent } from 'react';
import { ChevronDown, ChevronRight, CircleDollarSign, MoreHorizontal, RefreshCw, Search, Star } from 'lucide-react';
import { useVirtualizer } from '@tanstack/react-virtual';
import type { ListaSaldosProprios, LocalizacaoView, SaldoClienteLinha, SaldoProprioLinha } from '@pieltec/contracts';
import { Button } from '../../components/ui/button';
import { Card, CardContent } from '../../components/ui/card';
import { Checkbox } from '../../components/ui/checkbox';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '../../components/ui/dropdown-menu';
import { Input } from '../../components/ui/input';
import { Skeleton } from '../../components/ui/skeleton';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { TablePagination } from '../../components/ui/table-pagination';
import { formatarCustoUnitario, formatarQuantidadeEstoque, formatarValor } from '../../lib/estoque/formatacao';
import {
  LIMITE_VIRTUALIZACAO_SALDOS,
  TAMANHO_PAGINA_SALDOS,
  colunasVisiveis,
  escreverFiltrosNaUrl,
  estadoDaLista,
  filtrosSaldosAtivos,
  filtrosSaldosVazios,
  lerFiltrosDaUrl,
  montarQuerySaldosProprios,
  type ColunasVisiveis,
  type FiltrosSaldos,
  type TipoFiltroSaldos,
} from '../../lib/estoque/saldos';
import { PERMISSAO_MOVIMENTAR_ESTOQUE, type TipoMovimentacao } from '../../lib/estoque/movimentacao';
import {
  abaDaUrl,
  escreverFiltrosClientesNaUrl,
  filtrosClientesVazios,
  lerFiltrosClientesDaUrl,
  type AbaSaldos,
  type FiltrosSaldosClientes,
} from '../../lib/estoque/saldos-clientes';
import { obterSessaoAtual } from '../../lib/session';
import { formatarMoeda } from '../../lib/vendas/formatacao';
import { HistoricoLoteSheet, type LoteDoHistorico } from './historico-lote-sheet';
import { HistoricoMaterialSheet, type MaterialDoHistorico } from './historico-material-sheet';
import { NovaMovimentacaoSheet, type InicialMovimentacao, type InicialMovimentacaoLote } from './nova-movimentacao-sheet';
import { SaldosClientesAba } from './saldos-clientes-aba';
import { CampoSelect } from '../../components/ui/campo-select';

const CABECALHO = 'px-3 py-2 text-xs font-semibold uppercase tracking-wider';
const ROTULO = 'text-xs font-semibold uppercase tracking-wider text-muted-foreground';
const ALTURA_LINHA_ESTIMADA = 57;

function contagem(itensComSaldo: number): string {
  return itensComSaldo === 1 ? '1 item com saldo' : `${itensComSaldo} itens com saldo`;
}

function contagemLotes(total: number): string {
  return total === 1 ? '1 lote' : `${total} lotes`;
}

function buscaDaUrl(): string {
  return typeof window === 'undefined' ? '' : window.location.search;
}

function chaveLinha(linha: SaldoProprioLinha): string {
  return `${linha.materialId}-${linha.localizacaoId}`;
}

function larguraDaJanela(): number {
  return typeof window === 'undefined' ? 1280 : window.innerWidth;
}

/** Largura atual da janela: decide se Valor e Preferencial ficam na grade (>= 1024px) ou na linha expandida. */
function useColunasVisiveis(): ColunasVisiveis {
  const [largura, setLargura] = useState(larguraDaJanela);
  useEffect(() => {
    const atualizar = () => setLargura(larguraDaJanela());
    window.addEventListener('resize', atualizar);
    return () => window.removeEventListener('resize', atualizar);
  }, []);
  return colunasVisiveis(largura);
}

function Preferencial({ linha }: { linha: SaldoProprioLinha }) {
  return linha.preferencial ? (
    <span className="inline-flex items-center gap-1 text-xs font-semibold">
      <Star className="size-3" aria-hidden="true" />
      Preferencial
    </span>
  ) : (
    <span className="text-muted-foreground">—</span>
  );
}

function DadoExpandido({ rotulo, children }: { rotulo: string; children: React.ReactNode }) {
  return (
    <div>
      <dt className={ROTULO}>{rotulo}</dt>
      <dd className="text-sm">{children}</dd>
    </div>
  );
}

/** "Saldo por localização" do material: 1 ou N locais, lidos do servidor só quando a linha é expandida. */
function SaldoPorLocalizacao({ materialId }: { materialId: string }) {
  const [locais, setLocais] = useState<readonly SaldoProprioLinha[] | null>(null);
  const [erro, setErro] = useState(false);

  useEffect(() => {
    let vivo = true;
    const query = montarQuerySaldosProprios(filtrosSaldosVazios(), { materialId, tamanhoPagina: 100 });
    fetch(`/api/v1/estoque/saldos/proprio?${query}`, { credentials: 'include' })
      .then((res) => {
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        return res.json() as Promise<ListaSaldosProprios>;
      })
      .then((json) => {
        if (vivo) setLocais(json.itens);
      })
      .catch(() => {
        if (vivo) setErro(true);
      });
    return () => {
      vivo = false;
    };
  }, [materialId]);

  return (
    <section aria-label="Saldo por localização" className="flex flex-col gap-1">
      <h3 className={ROTULO}>Saldo por localização</h3>
      {erro ? (
        <p className="text-sm text-rose-700">Não foi possível carregar o saldo por localização.</p>
      ) : locais === null ? (
        <Skeleton className="h-8 w-full max-w-md" />
      ) : locais.length === 0 ? (
        <p className="text-sm text-muted-foreground">Nenhuma localização com saldo.</p>
      ) : (
        <table className="w-full max-w-md text-sm">
          <thead className="text-left">
            <tr>
              <th scope="col" className="py-1 pr-3 text-xs font-semibold uppercase tracking-wider text-muted-foreground">Localização</th>
              <th scope="col" className="py-1 pr-3 text-right text-xs font-semibold uppercase tracking-wider text-muted-foreground">Saldo</th>
              <th scope="col" className="py-1 text-xs font-semibold uppercase tracking-wider text-muted-foreground">Preferencial</th>
            </tr>
          </thead>
          <tbody>
            {locais.map((local) => (
              <tr key={local.localizacaoId} className="border-t">
                <td className="py-1 pr-3">{local.localizacaoNome}</td>
                <td className="py-1 pr-3 text-right tabular-nums">{formatarQuantidadeEstoque(local.saldo, local.unidade)}</td>
                <td className="py-1">
                  <Preferencial linha={local} />
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </section>
  );
}

interface LinhaSaldoProps {
  linha: SaldoProprioLinha;
  aberta: boolean;
  colunas: ColunasVisiveis;
  colSpan: number;
  onAlternar: () => void;
  onHistorico: (linha: SaldoProprioLinha) => void;
  /** Só `true` com estoque:movimentar: sem a permissão, as ações de movimentação não são renderizadas. */
  podeMovimentar: boolean;
  onMovimentar: (linha: SaldoProprioLinha, tipo: TipoMovimentacao) => void;
}

function LinhaSaldo({ linha, aberta, colunas, colSpan, onAlternar, onHistorico, podeMovimentar, onMovimentar }: LinhaSaldoProps) {
  const idDetalhe = `saldo-${chaveLinha(linha)}-detalhe`;

  function cliqueNaLinha(event: MouseEvent<HTMLTableRowElement>): void {
    if ((event.target as HTMLElement).closest('a,button,[role="menuitem"]')) return;
    onAlternar();
  }

  return (
    <Fragment>
      <tr onClick={cliqueNaLinha} className="cursor-pointer border-t hover:bg-slate-50">
        <td className="w-12 px-1 py-1">
          <button
            type="button"
            onClick={onAlternar}
            aria-expanded={aberta}
            aria-controls={idDetalhe}
            aria-label={`${aberta ? 'Recolher' : 'Expandir'} item ${linha.codigo}`}
            className="inline-flex size-11 items-center justify-center rounded-md hover:bg-slate-100 focus-visible:ring-[3px] focus-visible:ring-ring/50 focus-visible:outline-none"
          >
            {aberta ? <ChevronDown className="size-4" aria-hidden="true" /> : <ChevronRight className="size-4" aria-hidden="true" />}
          </button>
        </td>
        <td className="w-full max-w-0 px-3 py-2">
          <div className="flex items-center gap-2">
            <span className="min-w-0 truncate font-semibold tabular-nums" title={linha.codigo}>{linha.codigo}</span>
            {linha.semCustoMedio && (
              <span className="inline-flex shrink-0 items-center gap-1 rounded-full border border-amber-200 bg-amber-50 px-2 py-0.5 text-xs font-semibold text-amber-700">
                <CircleDollarSign className="size-3" aria-hidden="true" />
                Sem custo médio
              </span>
            )}
          </div>
          <div className="truncate text-muted-foreground" title={linha.nome}>{linha.nome}</div>
        </td>
        <td className="whitespace-nowrap px-3 py-2">{linha.localizacaoNome}</td>
        <td className="whitespace-nowrap px-3 py-2 text-right font-semibold tabular-nums">{formatarQuantidadeEstoque(linha.saldo, '')}</td>
        <td className="px-3 py-2">{linha.unidade}</td>
        <td className="whitespace-nowrap px-3 py-2 text-right tabular-nums">{formatarCustoUnitario(linha.custoMedio, linha.unidade)}</td>
        {colunas.valor && <td className="whitespace-nowrap px-3 py-2 text-right tabular-nums">{formatarValor(linha.valor)}</td>}
        {colunas.preferencial && (
          <td className="px-3 py-2">
            <Preferencial linha={linha} />
          </td>
        )}
        <td className="w-12 px-1 py-1 text-right">
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button type="button" variant="ghost" size="icon" aria-label={`Mais ações do item ${linha.codigo}`} className="size-11 sm:size-9">
                <MoreHorizontal aria-hidden="true" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuItem className="min-h-10" onSelect={() => onHistorico(linha)}>
                Histórico
              </DropdownMenuItem>
              {podeMovimentar && (
                <DropdownMenuItem className="min-h-10" onSelect={() => onMovimentar(linha, 'TRANSFERENCIA')}>
                  Transferir
                </DropdownMenuItem>
              )}
              {podeMovimentar && (
                <DropdownMenuItem className="min-h-10" onSelect={() => onMovimentar(linha, 'PERDA')}>
                  Registrar perda
                </DropdownMenuItem>
              )}
            </DropdownMenuContent>
          </DropdownMenu>
        </td>
      </tr>
      {aberta && (
        <tr id={idDetalhe} className="border-t bg-muted/50">
          <td colSpan={colSpan} className="px-4 py-3">
            <div className="flex flex-col gap-4">
              <dl className="grid grid-cols-2 gap-4 sm:grid-cols-3 lg:grid-cols-4">
                <DadoExpandido rotulo="Matéria-prima">
                  {linha.codigo} — {linha.nome}
                </DadoExpandido>
                <DadoExpandido rotulo="Cor">{linha.corCodigo ?? '—'}</DadoExpandido>
                {!colunas.valor && <DadoExpandido rotulo="Valor">{formatarValor(linha.valor)}</DadoExpandido>}
                {!colunas.preferencial && (
                  <DadoExpandido rotulo="Preferencial">
                    <Preferencial linha={linha} />
                  </DadoExpandido>
                )}
              </dl>
              {linha.semCustoMedio && (
                <p className="text-sm text-amber-700">
                  Este item tem saldo mas nenhuma entrada com custo. Faça uma entrada por NF de compra ou informe o custo no inventário de abertura.
                </p>
              )}
              <SaldoPorLocalizacao materialId={linha.materialId} />
              <div className="flex flex-wrap items-center gap-2">
                <Button type="button" variant="outline" size="sm" className="min-h-11 sm:min-h-8" onClick={() => onHistorico(linha)}>
                  Histórico
                </Button>
                {podeMovimentar && (
                  <Button type="button" variant="outline" size="sm" className="min-h-11 sm:min-h-8" onClick={() => onMovimentar(linha, 'TRANSFERENCIA')}>
                    Transferir
                  </Button>
                )}
                {podeMovimentar && (
                  <Button type="button" variant="outline" size="sm" className="min-h-11 sm:min-h-8" onClick={() => onMovimentar(linha, 'PERDA')}>
                    Registrar perda
                  </Button>
                )}
              </div>
            </div>
          </td>
        </tr>
      )}
    </Fragment>
  );
}

function Cabecalho({ colunas }: { colunas: ColunasVisiveis }) {
  return (
    <thead className="sticky top-0 z-10 bg-slate-100 text-left">
      <tr>
        <th scope="col" className="w-12 px-1 py-2">
          <span className="sr-only">Expandir</span>
        </th>
        <th scope="col" className={CABECALHO}>Matéria-prima</th>
        <th scope="col" className={CABECALHO}>Localização</th>
        <th scope="col" className={`${CABECALHO} text-right`}>Saldo</th>
        <th scope="col" className={CABECALHO}>Un.</th>
        <th scope="col" className={`${CABECALHO} text-right`}>Custo médio</th>
        {colunas.valor && <th scope="col" className={`${CABECALHO} text-right`}>Valor</th>}
        {colunas.preferencial && <th scope="col" className={CABECALHO}>Preferencial</th>}
        <th scope="col" className="w-12 px-1 py-2">
          <span className="sr-only">Ações</span>
        </th>
      </tr>
    </thead>
  );
}

interface GradeProps {
  linhas: readonly SaldoProprioLinha[];
  colunas: ColunasVisiveis;
  abertas: ReadonlySet<string>;
  onAlternar: (chave: string) => void;
  onHistorico: (linha: SaldoProprioLinha) => void;
  podeMovimentar: boolean;
  onMovimentar: (linha: SaldoProprioLinha, tipo: TipoMovimentacao) => void;
}

function Grade({ linhas, colunas, abertas, onAlternar, onHistorico, podeMovimentar, onMovimentar }: GradeProps) {
  const rolagemRef = useRef<HTMLDivElement>(null);
  const virtualizar = linhas.length > LIMITE_VIRTUALIZACAO_SALDOS;
  const colSpan = 7 + (colunas.valor ? 1 : 0) + (colunas.preferencial ? 1 : 0);

  // Hook incondicional: com count 0 não faz nada quando a grade é pequena.
  const virtualizador = useVirtualizer({
    count: virtualizar ? linhas.length : 0,
    getScrollElement: () => rolagemRef.current,
    estimateSize: () => ALTURA_LINHA_ESTIMADA,
    overscan: 8,
  });

  const renderLinha = (linha: SaldoProprioLinha) => (
    <LinhaSaldo
      key={chaveLinha(linha)}
      linha={linha}
      aberta={abertas.has(chaveLinha(linha))}
      colunas={colunas}
      colSpan={colSpan}
      onAlternar={() => onAlternar(chaveLinha(linha))}
      onHistorico={onHistorico}
      podeMovimentar={podeMovimentar}
      onMovimentar={onMovimentar}
    />
  );

  if (!virtualizar) {
    return (
      <table className="w-full text-sm">
        <caption className="sr-only">Saldo do estoque próprio por matéria-prima e localização</caption>
        <Cabecalho colunas={colunas} />
        <tbody>{linhas.map(renderLinha)}</tbody>
      </table>
    );
  }

  // Virtualizada: cada item é um <tbody> (uma ou duas linhas) medido; espaçadores mantêm a rolagem.
  const virtuais = virtualizador.getVirtualItems();
  const antes = virtuais.length > 0 ? (virtuais[0]?.start ?? 0) : 0;
  const depois = virtuais.length > 0 ? virtualizador.getTotalSize() - (virtuais[virtuais.length - 1]?.end ?? 0) : 0;

  return (
    <div ref={rolagemRef} className="max-h-[70vh] overflow-y-auto">
      <table className="w-full text-sm">
        <caption className="sr-only">Saldo do estoque próprio por matéria-prima e localização</caption>
        <Cabecalho colunas={colunas} />
        <tbody aria-hidden="true">
          <tr style={{ height: antes }} />
        </tbody>
        {virtuais.map((virtual) => {
          const linha = linhas[virtual.index];
          if (!linha) return null;
          return (
            <tbody key={chaveLinha(linha)} data-index={virtual.index} ref={virtualizador.measureElement}>
              {renderLinha(linha)}
            </tbody>
          );
        })}
        <tbody aria-hidden="true">
          <tr style={{ height: depois }} />
        </tbody>
      </table>
    </div>
  );
}

export function SaldosPage() {
  // A aba ativa e os filtros de cada aba vêm da URL; os filtros da aba que não está na URL começam vazios.
  const [aba, setAba] = useState<AbaSaldos>(() => abaDaUrl(buscaDaUrl()));
  const [filtros, setFiltros] = useState<FiltrosSaldos>(() =>
    abaDaUrl(buscaDaUrl()) === 'proprio' ? lerFiltrosDaUrl(buscaDaUrl()) : filtrosSaldosVazios(),
  );
  const [filtrosClientes, setFiltrosClientes] = useState<FiltrosSaldosClientes>(() =>
    abaDaUrl(buscaDaUrl()) === 'clientes' ? lerFiltrosClientesDaUrl(buscaDaUrl()) : filtrosClientesVazios(),
  );
  const [totalClientes, setTotalClientes] = useState<number | null>(null);
  const [buscaDigitada, setBuscaDigitada] = useState(filtros.busca);
  const [pagina, setPagina] = useState(1);
  const [tamanhoPagina, setTamanhoPagina] = useState(TAMANHO_PAGINA_SALDOS);
  const [dados, setDados] = useState<ListaSaldosProprios | null>(null);
  const [carregando, setCarregando] = useState(true);
  const [erro, setErro] = useState(false);
  const [localizacoes, setLocalizacoes] = useState<readonly LocalizacaoView[]>([]);
  const [abertas, setAbertas] = useState<ReadonlySet<string>>(new Set());
  const [historico, setHistorico] = useState<MaterialDoHistorico | null>(null);
  const [permissoes, setPermissoes] = useState<readonly string[]>([]);
  const [movimentacaoAberta, setMovimentacaoAberta] = useState(false);
  const [movimentacaoInicial, setMovimentacaoInicial] = useState<InicialMovimentacao | null>(null);
  const [movimentacaoLote, setMovimentacaoLote] = useState<InicialMovimentacaoLote | null>(null);
  const [historicoLote, setHistoricoLote] = useState<LoteDoHistorico | null>(null);
  const [recargaClientes, setRecargaClientes] = useState(0);
  const colunas = useColunasVisiveis();

  // Localizações para o filtro: se falhar, o filtro fica só com "Todas" (a lista de saldos não depende dele).
  useEffect(() => {
    let vivo = true;
    fetch('/api/v1/estoque/localizacoes', { credentials: 'include' })
      .then((res) => (res.ok ? (res.json() as Promise<readonly LocalizacaoView[]>) : []))
      .then((lista) => {
        if (vivo) setLocalizacoes(lista.filter((local) => local.ativa));
      })
      .catch(() => undefined);
    return () => {
      vivo = false;
    };
  }, []);

  // Permissões da sessão: só decidem o que é renderizado; a autorização real é do servidor.
  useEffect(() => {
    let vivo = true;
    void obterSessaoAtual().then((sessao) => {
      if (vivo) setPermissoes(sessao.autenticado ? sessao.usuario.permissions : []);
    });
    return () => {
      vivo = false;
    };
  }, []);

  // Aba e filtros persistem na URL (substitui a entrada do histórico: filtrar não empilha voltas).
  useEffect(() => {
    const busca = aba === 'clientes' ? escreverFiltrosClientesNaUrl(filtrosClientes) : escreverFiltrosNaUrl(filtros);
    window.history.replaceState(null, '', `${window.location.pathname}${busca}`);
  }, [aba, filtros, filtrosClientes]);

  const carregar = useCallback(
    async (sinal?: { vivo: boolean }) => {
      setCarregando(true);
      setErro(false);
      try {
        const res = await fetch(`/api/v1/estoque/saldos/proprio?${montarQuerySaldosProprios(filtros, { pagina, tamanhoPagina })}`, {
          credentials: 'include',
        });
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        const json = (await res.json()) as ListaSaldosProprios;
        if (!sinal || sinal.vivo) {
          setDados(json);
          setAbertas(new Set());
        }
      } catch {
        if (!sinal || sinal.vivo) setErro(true);
      } finally {
        if (!sinal || sinal.vivo) setCarregando(false);
      }
    },
    [filtros, pagina, tamanhoPagina],
  );

  // A aba "Material de clientes" carrega a própria lista; o estoque próprio só é lido enquanto a sua aba está ativa.
  useEffect(() => {
    if (aba !== 'proprio') return;
    const sinal = { vivo: true };
    void carregar(sinal);
    return () => {
      sinal.vivo = false;
    };
  }, [aba, carregar]);

  function alterarFiltros(parcial: Partial<FiltrosSaldos>): void {
    setFiltros((atual) => ({ ...atual, ...parcial }));
    setPagina(1);
  }

  function aplicarBusca(event: FormEvent): void {
    event.preventDefault();
    alterarFiltros({ busca: buscaDigitada.trim() });
  }

  function limparFiltros(): void {
    setBuscaDigitada('');
    setFiltros(filtrosSaldosVazios());
    setPagina(1);
  }

  function alterarFiltrosClientes(parcial: Partial<FiltrosSaldosClientes>): void {
    setFiltrosClientes((atual) => ({ ...atual, ...parcial }));
  }

  function alternar(chave: string): void {
    setAbertas((atual) => {
      const proximo = new Set(atual);
      if (proximo.has(chave)) proximo.delete(chave);
      else proximo.add(chave);
      return proximo;
    });
  }

  function abrirHistorico(linha: SaldoProprioLinha): void {
    setHistorico({ id: linha.materialId, codigo: linha.codigo, nome: linha.nome });
  }

  function abrirNovaMovimentacao(): void {
    setMovimentacaoInicial(null);
    setMovimentacaoLote(null);
    setMovimentacaoAberta(true);
  }

  function abrirHistoricoLote(linha: SaldoClienteLinha): void {
    setHistoricoLote({ id: linha.loteId, loteInterno: linha.loteInternoPieltec, clienteNome: linha.clienteNome, unidade: linha.unidade });
  }

  /** "Transferir" do lote: abre a Sheet em Transferir com o lote escolhido (transferência manual, exceção D-22). */
  function transferirLote(linha: SaldoClienteLinha): void {
    setMovimentacaoInicial(null);
    setMovimentacaoLote({
      lote: {
        id: linha.loteId,
        loteInternoPieltec: linha.loteInternoPieltec,
        clienteNome: linha.clienteNome,
        materialCodigo: linha.materialCodigo,
        saldo: linha.saldo,
        localizacaoNome: linha.localizacaoNome,
      },
      unidade: linha.unidade,
    });
    setMovimentacaoAberta(true);
  }

  function movimentarLinha(linha: SaldoProprioLinha, tipo: TipoMovimentacao): void {
    setMovimentacaoLote(null);
    setMovimentacaoInicial({
      tipo,
      materialId: linha.materialId,
      codigo: linha.codigo,
      nome: linha.nome,
      unidade: linha.unidade,
      localizacaoId: linha.localizacaoId,
    });
    setMovimentacaoAberta(true);
  }

  const podeMovimentar = permissoes.includes(PERMISSAO_MOVIMENTAR_ESTOQUE);
  const comFiltro = filtrosSaldosAtivos(filtros);
  const estado = estadoDaLista({ carregando, erro, itens: dados?.itens, filtrosAtivos: comFiltro });

  return (
    <div className="mx-auto w-full max-w-7xl p-6 flex flex-col gap-6">
      <Card>
        <CardContent className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
          <div className="flex flex-col gap-1">
            <h1 className="text-xl font-semibold leading-tight">Saldos de estoque</h1>
            <p className="text-sm text-muted-foreground" aria-live="polite">
              {aba === 'proprio' ? (dados ? contagem(dados.itensComSaldo) : ' ') : totalClientes === null ? ' ' : contagemLotes(totalClientes)}
            </p>
          </div>
          <div className="flex flex-col gap-1 sm:items-end">
            {podeMovimentar && (
              <div className="mb-2 flex flex-wrap gap-2 sm:justify-end">
                <Button asChild variant="secondary" className="min-h-11 sm:min-h-9">
                  <a href="/coletor/tinta">Abrir tela de pesagem</a>
                </Button>
                <Button type="button" variant="secondary" onClick={abrirNovaMovimentacao} className="min-h-11 sm:min-h-9">
                  Nova movimentação
                </Button>
              </div>
            )}
            {aba === 'proprio' && (
              <>
                <span className={ROTULO}>Valor do estoque próprio</span>
                <span className="text-[1.75rem] font-semibold leading-tight tabular-nums">{dados ? formatarMoeda(dados.valorTotal) : '—'}</span>
                <span className="text-sm text-muted-foreground">Custo médio ponderado. Não inclui material de clientes.</span>
              </>
            )}
          </div>
        </CardContent>
      </Card>

      <Tabs value={aba} onValueChange={(valor) => setAba(valor === 'clientes' ? 'clientes' : 'proprio')} className="gap-6">
        <TabsList aria-label="Dono do estoque">
          <TabsTrigger value="proprio" className="min-h-9 px-4">
            Estoque próprio
          </TabsTrigger>
          <TabsTrigger value="clientes" className="min-h-9 px-4">
            Material de clientes
          </TabsTrigger>
        </TabsList>

        <TabsContent value="proprio" className="flex flex-col gap-6">
          <div className="sticky top-0 z-20 flex flex-col gap-3 border-b bg-background/95 py-3 backdrop-blur" role="group" aria-label="Filtros da lista">
            <form onSubmit={aplicarBusca} role="search" className="flex items-center gap-2">
              <div className="relative flex-1">
                <Search className="absolute left-3 top-1/2 size-4 -translate-y-1/2 text-slate-400" aria-hidden="true" />
                <Input
                  type="search"
                  aria-label="Buscar no estoque"
                  placeholder="Buscar por código, descrição ou cor"
                  value={buscaDigitada}
                  onChange={(e) => setBuscaDigitada(e.target.value)}
                  className="pl-9"
                />
              </div>
              <Button type="submit" variant="secondary">
                Buscar
              </Button>
            </form>

            <div className="flex flex-wrap items-end gap-3">
              <div className="flex flex-col gap-1">
                <label htmlFor="filtro-localizacao" className={ROTULO}>
                  Localização
                </label>
                <CampoSelect
                  id="filtro-localizacao"
                  value={filtros.localizacaoId}
                  onValueChange={(valor) => alterarFiltros({ localizacaoId: valor })}
                  options={[{ value: '', label: 'Todas' }, ...localizacoes.map((local) => ({ value: local.id, label: local.nome }))]}
                  className="w-56"
                />
              </div>

              <div className="flex flex-col gap-1">
                <label htmlFor="filtro-tipo" className={ROTULO}>
                  Tipo
                </label>
                <CampoSelect
                  id="filtro-tipo"
                  value={filtros.tipo}
                  onValueChange={(valor) => alterarFiltros({ tipo: valor as TipoFiltroSaldos })}
                  options={[{ value: 'TODOS', label: 'Todos' }, { value: 'TINTA', label: 'Tinta' }, { value: 'OUTRAS', label: 'Outras matérias-primas' }]}
                  className="w-56"
                />
              </div>

              <label htmlFor="filtro-sem-saldo" className="flex min-h-[var(--control-height,2rem)] cursor-pointer items-center gap-2 text-sm">
                <Checkbox
                  id="filtro-sem-saldo"
                  checked={filtros.mostrarSemSaldo}
                  onCheckedChange={(marcada) => alterarFiltros({ mostrarSemSaldo: marcada === true })}
                />
                Mostrar itens sem saldo
              </label>

              {comFiltro && (
                <Button type="button" variant="ghost" onClick={limparFiltros}>
                  Limpar filtros
                </Button>
              )}
            </div>
          </div>

          {estado === 'erro' ? (
            <Card>
              <CardContent role="alert" className="flex flex-col items-center gap-4 py-12 text-center">
                <p className="text-sm text-rose-700">Não foi possível carregar o saldo de estoque. Verifique a conexão e tente novamente.</p>
                <Button type="button" variant="outline" onClick={() => void carregar()}>
                  <RefreshCw aria-hidden="true" />
                  Tentar novamente
                </Button>
              </CardContent>
            </Card>
          ) : estado === 'carregando' ? (
            <div className="flex flex-col gap-2" aria-busy="true" aria-label="Carregando saldo de estoque">
              {Array.from({ length: 8 }).map((_, i) => (
                <Skeleton key={i} className="h-10 w-full" />
              ))}
              <fieldset disabled className="opacity-60">
                <TablePagination
                  page={pagina}
                  pageSize={tamanhoPagina}
                  totalItems={dados?.total ?? 0}
                  onPageChange={setPagina}
                  onPageSizeChange={setTamanhoPagina}
                  itemLabel="itens"
                />
              </fieldset>
            </div>
          ) : estado === 'vazio-sem-saldo' ? (
            <Card>
              <CardContent className="flex flex-col items-center gap-2 py-12 text-center">
                <h2 className="text-xl font-semibold leading-tight">Nenhum estoque com saldo</h2>
                <p className="max-w-xl text-sm text-muted-foreground">
                  Por padrão só aparecem itens com saldo. Marque &quot;Mostrar itens sem saldo&quot; ou registre uma entrada pelo recebimento de uma NF de compra ou por um inventário de abertura.
                </p>
              </CardContent>
            </Card>
          ) : estado === 'vazio-filtro' ? (
            <Card>
              <CardContent className="flex flex-col items-center gap-2 py-12 text-center">
                <h2 className="text-xl font-semibold leading-tight">Nenhum item encontrado</h2>
                <p className="text-sm text-muted-foreground">Ajuste ou limpe os filtros.</p>
                <Button type="button" variant="outline" onClick={limparFiltros}>
                  Limpar filtros
                </Button>
              </CardContent>
            </Card>
          ) : (
            <Card className="overflow-hidden py-0 gap-0">
              <Grade
                linhas={dados?.itens ?? []}
                colunas={colunas}
                abertas={abertas}
                onAlternar={alternar}
                onHistorico={abrirHistorico}
                podeMovimentar={podeMovimentar}
                onMovimentar={movimentarLinha}
              />
              <TablePagination
                page={pagina}
                pageSize={tamanhoPagina}
                totalItems={dados?.total ?? 0}
                onPageChange={setPagina}
                onPageSizeChange={(tamanho) => {
                  setTamanhoPagina(tamanho);
                  setPagina(1);
                }}
                itemLabel="itens"
              />
            </Card>
          )}
        </TabsContent>

        <TabsContent value="clientes">
          <SaldosClientesAba
            filtros={filtrosClientes}
            aoAlterarFiltros={alterarFiltrosClientes}
            aoLimparFiltros={() => setFiltrosClientes(filtrosClientesVazios())}
            aoAtualizarTotal={setTotalClientes}
            podeMovimentar={podeMovimentar}
            aoHistorico={abrirHistoricoLote}
            aoTransferir={transferirLote}
            recarga={recargaClientes}
          />
        </TabsContent>
      </Tabs>

      <HistoricoMaterialSheet material={historico} localizacoes={localizacoes} onFechar={() => setHistorico(null)} />
      <HistoricoLoteSheet lote={historicoLote} onFechar={() => setHistoricoLote(null)} />
      {podeMovimentar && (
        <NovaMovimentacaoSheet
          aberto={movimentacaoAberta}
          aoFechar={() => setMovimentacaoAberta(false)}
          aoRegistrar={() => {
            if (aba === 'proprio') void carregar();
            setRecargaClientes((n) => n + 1);
          }}
          inicial={movimentacaoInicial}
          inicialLote={movimentacaoLote}
        />
      )}
    </div>
  );
}
